<script>
    // @check-js
    import { fade } from "svelte/transition";
    import { onMount } from "svelte";
    import Icon from "svelte-fa";
    import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
    import { faChevronUp } from "@fortawesome/free-solid-svg-icons";

    import { MENU_TYPES } from "./menu.lib";
    import SubMenu from "./SubMenu.svelte";
    import FullHr from "../../components/FullHr.svelte";
    import { trackNavigationClickTypes, trackNavigationClick } from "../../utils/track.utils.js";

    export let onSubmenuToggle = (w) => {};
    /** @type {import('./types').Menu} */
    export let menu = [];

    /** @type {string | null}*/
    let selectedMenuItemString = null; //"1-0";

    onMount(() => {
        const checkMenuClick = (e) => {
            if (!selectedMenuItemString) {
                return;
            }
            if (!e.target.closest(".submenu")) {
                toggleMenu();
            }
        };

        document.addEventListener("click", checkMenuClick);
        return () => document.removeEventListener("click", checkMenuClick);
    });

    /**
     *
     * @param {string} selectionString
     */
    const toggleMenu = (selectionString = "") => {
        if (selectedMenuItemString === selectionString) {
            selectedMenuItemString = null;
        } else {
            selectedMenuItemString = selectionString;
        }
        onSubmenuToggle(!!selectedMenuItemString);
    };

    $: isSelectedSubmenu = (menuIndex, sectionIndex) => {
        return selectedMenuItemString === `${menuIndex}-${sectionIndex}`;
    };
</script>

<div class="outer-menu" data-sveltekit-preload-data="hover">
    <nav>
        <ul>
            {#each menu as section, menuIndex}
                {#each section as topLevelItem, sectionIndex}
                    <li
                        class:section-end={sectionIndex === section.length - 1 && menuIndex !== menu.length - 1}
                        class:selected={isSelectedSubmenu(menuIndex, sectionIndex)}
                        aria-label={topLevelItem.name}
                    >
                        <div class="top-level-item">
                            {#if topLevelItem.type === MENU_TYPES.LINK}
                                <a
                                    {...topLevelItem.attributes || {}}
                                    href={topLevelItem.url}
                                    class="toplevel-link"
                                    on:click={() =>
                                        trackNavigationClick(trackNavigationClickTypes.MENU_CLICK, topLevelItem.name)}
                                    >{topLevelItem.name}</a
                                >
                            {:else if topLevelItem.type === MENU_TYPES.SUBMENU}
                                <button
                                    class="submenu focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-white rounded-sm"
                                    tabindex="0"
                                    on:click={() => toggleMenu(`${menuIndex}-${sectionIndex}`)}
                                >
                                    {#if topLevelItem.icon}
                                        <Icon icon={topLevelItem.icon} />
                                    {/if}
                                    <span>{topLevelItem.name}</span>
                                    {#if isSelectedSubmenu(menuIndex, sectionIndex)}
                                        <Icon icon={faChevronUp} />
                                    {:else}
                                        <Icon class="pointer-events-none" icon={faChevronDown} />
                                    {/if}
                                </button>
                            {:else}{topLevelItem.name}{/if}
                        </div>
                        {#if topLevelItem.type === MENU_TYPES.SUBMENU}
                            <div
                                transition:fade|global
                                class:hidden={selectedMenuItemString !== `${menuIndex}-${sectionIndex}`}
                                class:active={selectedMenuItemString === `${menuIndex}-${sectionIndex}`}
                                class="submenu-section"
                            >
                                {#if topLevelItem.children[0].type !== MENU_TYPES.SUBMENU_SECTION}
                                    <SubMenu submenu={topLevelItem.children} />
                                {:else}
                                    <div class="submenu-section-items">
                                        {#each topLevelItem.children as activeSection}
                                            <div class="submenu-section-item">
                                                <p>{activeSection.name} <br /> <span>{activeSection.subtitle}</span></p>
                                                <FullHr />
                                                <SubMenu
                                                    submenu={activeSection.children}
                                                    singleColumn={true}
                                                    showListStyle={false}
                                                />
                                            </div>
                                        {/each}
                                    </div>
                                {/if}
                            </div>
                        {/if}
                    </li>
                {/each}
            {/each}
        </ul>
    </nav>
</div>

<style>
    a,
    a:hover {
        color: white;
        text-decoration: none;
    }
    .outer-menu {
        width: auto;
        height: 55px;
        padding: 10px 0 10px 0;
        background: rgb(42, 42, 42);
        background: linear-gradient(270deg, rgba(42, 42, 42, 1) 0%, rgba(73, 73, 73, 1) 50%, rgba(42, 42, 42, 1) 100%);
        flex: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
    }
    nav {
        font-size: 15px;
        text-decoration: none;
        color: white;
        overflow-x: auto;
    }
    nav ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-wrap: nowrap;
    }
    nav li {
        flex: 1;
        padding: 4px 8px;
        display: inline-block;
        width: auto;
        user-select: none;
    }
    *:active {
        outline: none;
    }
    nav li > .top-level-item {
        padding: 4px 8px;
        border-radius: 3px;
    }
    nav li.selected > .top-level-item {
        background: rgba(50, 50, 50, 0.9);
    }

    .section-end {
        /*border-right: 1px solid rgba(200, 200, 200, 0.7);*/
        margin-right: 2rem;
    }
    .toplevel-link {
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.9rem;
        white-space: nowrap;
    }
    .submenu {
        display: inline;
        cursor: pointer;
        padding-right: 2px;
        display: flex;
        flex-direction: row;
        align-items: center;
        white-space: nowrap;
    }
    .submenu:focus {
        outline: unset;
    }
    .submenu > span {
        padding-left: 5px;
        padding-right: 4px;
        font-size: 1.1rem;
    }
    .submenu-section {
        min-height: 50px;
        padding: 25px 10px;
        font-size: 15px;
        background: rgb(17, 17, 17);
        background: linear-gradient(270deg, rgba(17, 17, 17, 1) 0%, rgba(43, 43, 43, 1) 50%, rgba(17, 17, 17, 1) 100%);
        top: 55px;
        left: 20%;
        right: 20%;
        min-width: 786px;
        position: absolute;
        z-index: 20;
    }
    .submenu-section-items {
        display: flex;
        justify-content: space-between;
    }
    .submenu-section-item {
        text-align: left;
    }
    .submenu-section-item p {
        padding-left: 20px;
        margin: 0;
        font-size: 1.2rem;
    }
    .submenu-section-item span {
        font-size: 0.8rem;
        font-weight: 400;
        white-space: nowrap;
    }
    .hidden {
        visibility: hidden;
        opacity: 0;
        pointer-events: none;
        z-index: 0;
    }
    .active {
        visibility: visible;
        opacity: 1;
        transition: 0.3s;
        pointer-events: unset;
        z-index: unset;
    }
</style>
